import { useState, type MouseEvent } from 'react';
import useSWR from 'swr';
import { useAtomValue } from 'jotai';
import { usePathname } from 'next/navigation';
import { setCookie } from 'nookies';

import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useUrl } from '@packages/shared/src/hooks/useUrl/useUrl';
import { useIsHydrated } from '@packages/shared/src/hooks/useIsHydrated/useIsHydrated';
import type { CookieOptions } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { getFullLocaleFromConfig, localizedPathnameCacheKey } from '@packages/config/src/default';
import type { Language, Locale, LocalizedPath } from '@packages/config/src/default';
import { localizeUrl } from '@packages/shared/src/utils/localizeUrl/localizeUrl';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { GTMEventGlycerinButtonClick } from '@packages/tracking/src/types/events';

import { languageAlternatesAtom } from './useLocaleSwitch.atom';

/**
 * Strips the language prefix from a pathname, returning the canonical path.
 * e.g. '/fr/mon-compte/apercu' → '/mon-compte/apercu'
 *      '/mein-konto/uebersicht' → '/mein-konto/uebersicht'
 */
const stripLanguagePrefix = (pathname: string, languages: string[]): string => {
  const regex = new RegExp(`^/?(${languages.join('|')})(/|$)`);
  return pathname.replace(regex, '/').replace(/\/\/+/, '/') || '/';
};

/**
 * Checks if the current pathname matches any path defined in the order config.
 * If a match is found, returns the localized equivalent for the target locale
 * (without the language prefix — localizeUrl handles that separately).
 *
 * Uses the longest (most specific) match to correctly distinguish e.g.
 * '/mein-konto' (base) from '/mein-konto/uebersicht' (overview).
 */
const findLocalizedOrderPath = (
  pathname: string,
  orderPaths: Record<string, LocalizedPath>,
  targetLocale: Locale,
  languages: string[],
): string | undefined => {
  const canonicalPath = stripLanguagePrefix(pathname, languages);

  let bestMatch: { configuredPath: string; pathConfig: LocalizedPath } | undefined;

  for (const pathConfig of Object.values(orderPaths)) {
    for (const configuredPath of Object.values(pathConfig).filter(Boolean)) {
      if (canonicalPath === configuredPath || canonicalPath.startsWith(`${configuredPath}/`)) {
        // Keep the most specific (longest) match to avoid e.g. '/mein-konto' shadowing '/mein-konto/uebersicht'
        if (!bestMatch || configuredPath.length > bestMatch.configuredPath.length) {
          bestMatch = { configuredPath, pathConfig };
        }
      }
    }
  }

  if (!bestMatch) return undefined;

  const { configuredPath, pathConfig } = bestMatch;
  const subPath = canonicalPath.slice(configuredPath.length);
  const localizedBase = pathConfig[targetLocale] ?? pathConfig.default;
  return `${localizedBase}${subPath}`;
};

export const useLocaleSwitch = () => {
  const [isSwitching, setIsSwitching] = useState(false);

  // Hide the URL on initial SSR, because the page-provided alternates are only available on the client when using the app router
  // If we show the default language-changed pathname, it will be incorrect for some routes, and possibly lead to SEO index pollution
  // With this delay, we allow the page enough time to set the atom with the correct alternates
  const { isHydrated: showUrl } = useIsHydrated();

  const config = useConfig();
  const {
    i18n: { languages },
  } = useConfig();
  const { language } = useI18n();
  const pathname = usePathname();
  const url = useUrl();
  const dispatch = useTracking();

  const [switchedFromLanguage, setSwitchedFromLanguage] = useState(language);
  if (language !== switchedFromLanguage) {
    setSwitchedFromLanguage(language);
    setIsSwitching(false);
  }

  const { data: localizedPathnameFromPagesRouter } = useSWR(localizedPathnameCacheKey, {
    // prevent fetching newer data and use only stale data (https://swr.vercel.app/docs/revalidation#disable-automatic-revalidations)
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const COOKIE_OPTIONS: CookieOptions = {
    path: '/',
    maxAge: 90 * 24 * 60 * 60, // 90 days in seconds
    secure: true,
    sameSite: 'lax',
    httpOnly: false,
  };

  const languageTitle: Partial<Record<Language, string>> = {
    fr: 'Français',
    de: 'Deutsch',
  };

  const targetLanguage: Language = languages.find((l: string) => l !== language) ?? languages[0];
  const targetLocale = getFullLocaleFromConfig(targetLanguage, config);

  const languageAlternates = useAtomValue(languageAlternatesAtom);
  const localizedPathnameFromAppRouter =
    languageAlternates?.currentPathname === pathname
      ? languageAlternates?.alternateLinks?.[targetLanguage]
      : undefined;

  const localizedOrderPath =
    pathname != null
      ? findLocalizedOrderPath(pathname, config.order.paths, targetLocale, languages)
      : undefined;

  const href = showUrl
    ? localizeUrl(
        localizedOrderPath ||
          localizedPathnameFromAppRouter ||
          (localizedPathnameFromPagesRouter ?? url),
        targetLanguage,
        config,
      )
    : undefined;

  const switchLocale = (e?: MouseEvent) => {
    e?.preventDefault();
    setIsSwitching(true);

    // Track the language switch as a standardized ISO 639-1 language code (e.g. 'de', 'fr'),
    // to keep the format consistent with the app team's SDK tracking.
    // Dispatched before navigation so the event is pushed to the dataLayer while the page is still alive.
    dispatch<GTMEventGlycerinButtonClick>({
      event: 'ButtonClick',
      ButtonClickData: {
        category: 'language_switch',
        detail: targetLanguage,
        target: href,
      },
    });

    setCookie(null, 'PreferredLanguage', targetLanguage, COOKIE_OPTIONS);
    // Set PreferredLocale with legacy parameters for consistent behaviour with the legacy checkout.
    // Can be removed after nextjs checkout is live
    setCookie(
      null,
      'PreferredLocale',
      getFullLocaleFromConfig(targetLanguage, config).replace('-', '_'),
      {
        ...COOKIE_OPTIONS,
      },
    );
    if (href) {
      window.location.replace(href);
    }
  };

  return {
    label: languageTitle[targetLanguage],
    targetLanguage,
    href,
    switchLocale,
    isSwitching,
  };
};
